import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAnalyticsPayments } from '../../api/client';
import type {
  AnalyticsPeriod,
  PaymentsAnalyticsResponse,
  PaymentsByType,
  PaymentTypeTotals,
} from '../../api/types';
import { AnalyticsQueryState } from '../../analytics/AnalyticsQueryState';
import { PeriodGranularityControls } from '../../analytics/PeriodGranularityControls';
import { formatUsdFromCents, formatUnixDate } from '../../analytics/formatAnalytics';
import { parsePeriod } from '../../analytics/analyticsParams';
import { PAYMENT_TYPE_LABELS, PAYMENT_TYPE_ORDER } from './paymentsTypeLabels';
import * as styles from './analyticsPageStyles';

function emptyPaymentsByType(): PaymentsByType {
  const z: PaymentTypeTotals = { totalCents: 0, count: 0 };
  return {
    subscription: { ...z },
    pack: { ...z },
    refund: { ...z },
    dispute: { ...z },
    unknown: { ...z },
  };
}

export function PaymentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'));
  const teamIdFilter = (searchParams.get('teamId') ?? '').trim();

  const [teamInput, setTeamInput] = useState(teamIdFilter);

  const setPeriod = useCallback(
    (p: AnalyticsPeriod) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('period', String(p));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const applyTeamFilter = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const v = teamInput.trim();
        if (v) {
          next.set('teamId', v);
        } else {
          next.delete('teamId');
        }
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams, teamInput]);

  const clearTeamFilter = useCallback(() => {
    setTeamInput('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('teamId');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useEffect(() => {
    setTeamInput(teamIdFilter);
  }, [teamIdFilter]);

  const [data, setData] = useState<PaymentsAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getAnalyticsPayments({
      period,
      teamId: teamIdFilter || undefined,
    })
      .then(setData)
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Unable to load payment data. Please try again.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [period, teamIdFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const history = data?.paymentHistory ?? [];
  const paymentsByType = data?.paymentsByType ?? emptyPaymentsByType();
  const isEmpty =
    (data?.revenueInPeriod ?? 0) === 0 &&
    (data?.paymentsInPeriod ?? 0) === 0 &&
    history.length === 0;

  return (
    <div data-testid="analytics-payments-root">
      <PeriodGranularityControls period={period} onPeriodChange={setPeriod} />

      <div style={styles.filterRow}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14 }}>
          Workspace (team ID)
          <input
            type="text"
            style={styles.input}
            value={teamInput}
            onChange={(e) => setTeamInput(e.target.value)}
            placeholder="Optional filter"
            data-testid="analytics-payments-team-filter"
          />
        </label>
        <button type="button" style={styles.secondaryButton} onClick={applyTeamFilter}>
          Apply filter
        </button>
        {teamIdFilter ? (
          <button type="button" style={styles.secondaryButton} onClick={clearTeamFilter}>
            Clear workspace
          </button>
        ) : null}
      </div>

      <AnalyticsQueryState loading={loading} error={error} onRetry={load} isEmpty={isEmpty}>
        <div style={styles.card} data-testid="analytics-payments-summary">
          <p style={styles.sectionTitle}>Aggregate metrics</p>
          <div style={styles.metricGrid}>
            {PAYMENT_TYPE_ORDER.filter(
              (key) => key !== 'unknown' || (paymentsByType.unknown?.count ?? 0) > 0
            ).map((key) => {
              const row = paymentsByType[key];
              return (
                <div style={styles.metric} key={key} data-testid={`analytics-payments-type-${key}`}>
                  <div style={styles.metricLabel}>{PAYMENT_TYPE_LABELS[key]}</div>
                  <div style={styles.metricValue}>{formatUsdFromCents(row?.totalCents)}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    {row?.count ?? 0} {row?.count === 1 ? 'transaction' : 'transactions'}
                  </div>
                </div>
              );
            })}
            <div
              style={{
                ...styles.metric,
                gridColumn: '1 / -1',
                border: '2px solid #d1d5db',
              }}
            >
              <div style={styles.metricLabel}>Net total (period)</div>
              <div style={styles.metricValue}>{formatUsdFromCents(data?.revenueInPeriod)}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                {data?.paymentsInPeriod ?? 0} payment rows
              </div>
            </div>
          </div>
          <p style={styles.note}>
            Refunds and disputes are stored as positive amounts in Stripe; here they are shown as
            negative for net revenue. Per-workspace lines also appear on the client detail page.
            Totals respect the selected period
            {teamIdFilter ? ` and workspace ${teamIdFilter}` : ''}.
          </p>
        </div>

        <div style={styles.card}>
          <p style={styles.sectionTitle}>
            Payment history{teamIdFilter ? ` (${teamIdFilter})` : ''}
          </p>
          {history.length === 0 ? (
            <p style={styles.note}>No payment rows in this view for the selected filters.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table data-testid="analytics-payments-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Workspace</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.paymentId ?? `${row.teamId}-${row.createdAt}`}>
                      <td>{formatUnixDate(row.createdAt)}</td>
                      <td>{PAYMENT_TYPE_LABELS[row.type ?? 'unknown']}</td>
                      <td>{formatUsdFromCents(row.amount)}</td>
                      <td>
                        <span style={{ fontWeight: 500 }}>{row.teamName ?? '—'}</span>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          <code>{row.teamId}</code>
                        </div>
                      </td>
                      <td>{row.description ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </AnalyticsQueryState>
    </div>
  );
}
